import React, { useEffect, useRef, useState } from 'react'
import { MDBIcon } from 'mdb-react-ui-kit'
import AdminApi from './../../../../api/admin-api/admin-api.js'
import EliteAgencyTeamService from './../../../../shared/services/EliteAgencyTeam.service.js'
import { BOARDS, PERIODS, METRICS, GROUPINGS, CATEGORIES, ROTATIONS, REFRESHES } from './../../store/BigScreen.store'
import './BigScreenSettings.scss'

const labelOf = (options, value) => (options.find(o => `${o.value}` === `${value}`) || {}).label

const Select = ({ id, label, value, options, onChange }) => (
  <div className='big-screen-field'>
    <label htmlFor={id}>{label}</label>
    <select id={id} value={value} onChange={e => onChange(e.target.value)}>
      {options.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
)

// Every division (Sig Agents) or Elite Agency Team (Career Agents), so a filter
// that arrives in the page address is always one of the listed choices.
const useTeamOptions = group => {
  const [options, setOptions] = useState([])

  useEffect(() => {
    let cancelled = false
    setOptions([])

    const load =
      group === 'user_id'
        ? AdminApi.getDivisions().then(res => (res?.data?.data || []).map(d => ({ value: `${d.id}`, label: d.d_name })))
        : group === 'sub_agent_id'
        ? EliteAgencyTeamService.search({ pagination: false }).then(res =>
            (res?.models || res?.data || res?.payload || []).map(t => ({ value: `${t.id}`, label: t.team_name }))
          )
        : Promise.resolve([])

    load
      .then(list => !cancelled && setOptions(list.sort((a, b) => `${a.label}`.localeCompare(`${b.label}`))))
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [group])

  return options
}

// Every change is applied immediately through onChange, which writes it to the page address.
const BigScreenSettings = ({ settings, onChange, onClose }) => {
  const panelRef = useRef(null),
    teamOptions = useTeamOptions(settings.group)
  const update = changes => onChange({ ...settings, ...changes })

  useEffect(() => {
    panelRef.current?.focus()
    const onKey = e => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const moveBoard = (index, offset) => {
    const boards = [...settings.boards]
    const [board] = boards.splice(index, 1)
    boards.splice(index + offset, 0, board)
    update({ boards })
  }

  const unusedBoards = BOARDS.filter(b => !settings.boards.includes(b.value))

  return (
    <aside
      className='big-screen-settings'
      role='dialog'
      aria-modal='true'
      aria-labelledby='BigScreenSettingsTitle'
      tabIndex={-1}
      ref={panelRef}
    >
      <header>
        <h2 id='BigScreenSettingsTitle'>Big Screen Settings</h2>
        <button type='button' className='big-screen-icon-btn' onClick={onClose} aria-label='Close settings'>
          <MDBIcon icon='times' />
        </button>
      </header>

      <section>
        <h3>Boards</h3>
        <ol className='big-screen-board-list'>
          {settings.boards.map((board, i) => (
            <li key={board}>
              <span>{labelOf(BOARDS, board)}</span>
              <button
                type='button'
                className='big-screen-icon-btn'
                disabled={i === 0}
                onClick={() => moveBoard(i, -1)}
                aria-label={`Move ${labelOf(BOARDS, board)} up`}
              >
                <MDBIcon icon='arrow-up' />
              </button>
              <button
                type='button'
                className='big-screen-icon-btn'
                disabled={i === settings.boards.length - 1}
                onClick={() => moveBoard(i, 1)}
                aria-label={`Move ${labelOf(BOARDS, board)} down`}
              >
                <MDBIcon icon='arrow-down' />
              </button>
              <button
                type='button'
                className='big-screen-icon-btn'
                disabled={settings.boards.length === 1}
                onClick={() => update({ boards: settings.boards.filter(b => b !== board) })}
                aria-label={`Remove ${labelOf(BOARDS, board)}`}
              >
                <MDBIcon icon='trash-alt' />
              </button>
            </li>
          ))}
        </ol>
        {unusedBoards.length > 0 && (
          <div className='big-screen-field'>
            <label htmlFor='BigScreenAddBoard'>Add a board</label>
            <select
              id='BigScreenAddBoard'
              value=''
              onChange={e => e.target.value && update({ boards: [...settings.boards, e.target.value] })}
            >
              <option value=''>Choose a board…</option>
              {unusedBoards.map(b => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </section>

      <section>
        <h3>Display</h3>
        <Select
          id='BigScreenPeriod'
          label='Period'
          value={settings.period}
          options={PERIODS}
          onChange={period => update({ period })}
        />
        <Select
          id='BigScreenRotate'
          label='Rotate boards every'
          value={settings.rotate}
          options={ROTATIONS}
          onChange={rotate => update({ rotate: parseInt(rotate, 10) })}
        />
        <Select
          id='BigScreenRefresh'
          label='Refresh every'
          value={settings.refresh}
          options={REFRESHES}
          onChange={refresh => update({ refresh: parseInt(refresh, 10) })}
        />
      </section>

      {settings.boards.includes('leaderboard') && (
        <section>
          <h3>Leaderboard filters</h3>
          <Select
            id='BigScreenMetric'
            label='Measure'
            value={settings.metric}
            options={METRICS}
            onChange={metric => update({ metric })}
          />
          <Select
            id='BigScreenGroup'
            label='Rank'
            value={settings.group}
            options={GROUPINGS}
            onChange={group => update({ group, division_id: '', team_id: '' })}
          />
          <Select
            id='BigScreenCategory'
            label='Category'
            value={settings.category}
            options={[{ value: '', label: 'All categories' }, ...CATEGORIES.map(c => ({ value: c, label: c }))]}
            onChange={category => update({ category })}
          />
          {settings.metric === 'av' && (
            <div className='big-screen-field is-checkbox'>
              <input
                id='BigScreenNoAca'
                type='checkbox'
                checked={settings.no_aca}
                onChange={() => update({ no_aca: !settings.no_aca })}
              />
              <label htmlFor='BigScreenNoAca'>Remove ACA from AV totals</label>
            </div>
          )}
          {settings.group === 'user_id' && (
            <Select
              id='BigScreenDivision'
              label='Division'
              value={settings.division_id}
              options={[{ value: '', label: 'All Divisions' }, ...teamOptions]}
              onChange={division_id => update({ division_id })}
            />
          )}
          {settings.group === 'sub_agent_id' && (
            <Select
              id='BigScreenTeam'
              label='Elite Agency Team'
              value={settings.team_id}
              options={[{ value: '', label: 'All Elite Agency Teams' }, ...teamOptions]}
              onChange={team_id => update({ team_id })}
            />
          )}
        </section>
      )}
    </aside>
  )
}

export default BigScreenSettings
